import { useState } from "react";

import type { BrandSideCartItem } from "../../../components/shared/BrandSideCart";
import { useCompass } from "../../../contexts/CompassContext";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import { faireProductToCompassProduct } from "../../../view_models/compassProductFromEntity";

import {
  type BrandGroupSummary,
  resolveBrandGroupSummary,
} from "./BrandGroupHeader";

export interface BrandCart {
  cartQuantityFor: (productToken: string) => number;
  cartSubtotalForBrand: (brandName: string) => number;
  cartItemCountForBrand: (brandName: string) => number;
  /** Whether the side cart column is open. */
  isSideCartOpen: boolean;
  /** Brand shown in the side cart; kept after close so the panel can slide out. */
  sideCartBrandName: string | null;
  sideCartBrand: BrandGroupSummary | null;
  sideCartItems: BrandSideCartItem[];
  /** Opens the brand's side cart, or closes it when it is already open. */
  toggleSideCart: (brandName: string) => void;
  closeSideCart: () => void;
  handleQuickAdd: (product: FaireProductEntity) => void;
  handleQuickAddIncrement: (product: FaireProductEntity, quantity: number) => void;
  handleQuickAddDecrement: (product: FaireProductEntity, quantity: number) => void;
  setCartQuantity: (productToken: string, quantity: number) => void;
}

interface UseBrandCartOptions {
  /**
   * In the Brands view, changing a quantity opens the side cart for that
   * brand when it is closed. Off in the Products view, where the cart stays
   * closed.
   */
  openOnQuantityChange?: boolean;
}

/**
 * Per-brand cart state for the saved-products surfaces: quick-add handlers
 * backed by the Compass cart, plus the brand side cart's open state. The side
 * cart follows whichever brand's quantities the retailer changes, opening
 * first when `openOnQuantityChange` is set.
 *
 * `products` is the pool used to resolve the side cart brand's lockup facts.
 */
export const useBrandCart = (
  products: FaireProductEntity[],
  { openOnQuantityChange = false }: UseBrandCartOptions = {}
): BrandCart => {
  const {
    state: compassState,
    addToCart,
    updateCartQuantity,
  } = useCompass();
  const [sideCartBrandName, setSideCartBrandName] = useState<string | null>(
    null
  );
  const [isSideCartOpen, setIsSideCartOpen] = useState(false);

  const cartItems = compassState.cartItems;
  const itemsForBrand = (brandName: string) =>
    cartItems.filter((item) => item.product.brandName === brandName);

  const cartQuantityFor = (productToken: string): number =>
    cartItems.find((item) => item.product.token === productToken)?.quantity ??
    0;
  const cartSubtotalForBrand = (brandName: string): number =>
    itemsForBrand(brandName).reduce(
      (subtotal, item) =>
        subtotal +
        (item.product.priceDollars ?? item.product.price) * item.quantity,
      0
    );
  const cartItemCountForBrand = (brandName: string): number =>
    itemsForBrand(brandName).reduce((count, item) => count + item.quantity, 0);

  const closeSideCart = () => setIsSideCartOpen(false);
  const toggleSideCart = (brandName: string) => {
    if (isSideCartOpen && sideCartBrandName === brandName) {
      closeSideCart();
      return;
    }
    setSideCartBrandName(brandName);
    setIsSideCartOpen(true);
  };

  const syncSideCartToBrand = (brandName: string) => {
    if (!isSideCartOpen) {
      if (openOnQuantityChange) {
        setSideCartBrandName(brandName);
        setIsSideCartOpen(true);
      }
      return;
    }
    if (brandName !== sideCartBrandName) {
      setSideCartBrandName(brandName);
    }
  };
  const handleQuickAdd = (product: FaireProductEntity) => {
    addToCart(faireProductToCompassProduct(product), 1);
    syncSideCartToBrand(product.brandName);
  };
  const handleQuickAddIncrement = (
    product: FaireProductEntity,
    quantity: number
  ) => {
    updateCartQuantity(product.token, quantity + 1);
    syncSideCartToBrand(product.brandName);
  };
  const handleQuickAddDecrement = (
    product: FaireProductEntity,
    quantity: number
  ) => {
    updateCartQuantity(product.token, quantity - 1);
    syncSideCartToBrand(product.brandName);
  };

  const sideCartBrand = sideCartBrandName
    ? resolveBrandGroupSummary(
        sideCartBrandName,
        products.filter((product) => product.brandName === sideCartBrandName)
      )
    : null;
  const sideCartItems: BrandSideCartItem[] = sideCartBrandName
    ? itemsForBrand(sideCartBrandName).map((item) => ({
        token: item.product.token ?? item.product.id,
        name: item.product.name,
        imageUrl: item.product.imageUrl,
        unitPrice: item.product.priceDollars ?? item.product.price,
        quantity: item.quantity,
      }))
    : [];

  return {
    cartQuantityFor,
    cartSubtotalForBrand,
    cartItemCountForBrand,
    isSideCartOpen,
    sideCartBrandName,
    sideCartBrand,
    sideCartItems,
    toggleSideCart,
    closeSideCart,
    handleQuickAdd,
    handleQuickAddIncrement,
    handleQuickAddDecrement,
    setCartQuantity: updateCartQuantity,
  };
};
